/**
 * The map cards' badges (spec 025 D-6). "Missing media" comes from the
 * story's saved picture map (spec 026: a card whose scene has a place of a
 * needed picture that shows none) and from this session's story conversation: each `missingMedia`
 * entry (sounds since 026) whose `scene` names a current scene badges that
 * card; the others (no scene, or a name no longer in the story) go on one
 * "Missing media" chip above the map.
 * "Needs a fix" marks a card whose node has a `validateGraph` problem (010).
 */

import type { ChoiceOption, PictureMap, StoryApiResponse } from '../../../../Shared/src/@types';
import { isSlotNeeded } from '../../../../Shared/src/pictures';
import { storySceneIdOfKey } from '../../../../Shared/src/storyOperations/document';
import type { MissingMediaItem } from '../../api/client';
import { validateGraph } from '../../../../Shared/src/utils/validateGraph';
import { graphScenesOf } from '../../stores/storyStore';
import type { CardBadges } from './StoryMap';

export interface StudioBadges {
  badges: Map<string, CardBadges>;
  /** Missing-media entries that badge no card: the map's chip. */
  storyWideMissing: number;
}

export function studioBadges(
  story: Pick<StoryApiResponse, 'graph' | 'storyScenes'>,
  sceneChoicesMap: Map<number, ChoiceOption[]>,
  messages: ReadonlyArray<{ missingMedia?: MissingMediaItem[] }>,
  pictureMap?: PictureMap | null,
): StudioBadges {
  const { graph } = story;
  const scenes = graphScenesOf(story, sceneChoicesMap);
  const idsByName = new Map<string, string[]>();
  for (const n of graph.nodes) {
    if (n.type === 'end') continue;
    const name = scenes[n.storySceneId]?.name;
    if (name !== undefined) idsByName.set(name, [...(idsByName.get(name) ?? []), n.id]);
  }

  const badges = new Map<string, CardBadges>();
  const mark = (id: string, b: CardBadges) => badges.set(id, { ...badges.get(id), ...b });
  let storyWideMissing = 0;
  for (const m of messages) {
    for (const item of m.missingMedia ?? []) {
      const ids = item.scene ? idsByName.get(item.scene) : undefined;
      if (ids?.length) ids.forEach((id) => mark(id, { missingMedia: true }));
      else storyWideMissing += 1;
    }
  }

  // Spec 026: a scene where a place of a needed picture shows none (D-19: a
  // scene whose places of the slot all show a picture is not missing any).
  const needing = new Set<number>();
  for (const slot of pictureMap?.slots ?? []) {
    if (!isSlotNeeded(slot)) continue;
    for (const use of slot.uses) {
      if (use.kind === 'line' || (typeof use.mediaId === 'number' && use.mediaId > 0)) continue;
      const id = storySceneIdOfKey(use.scene);
      if (id !== undefined) needing.add(id);
    }
  }
  if (needing.size) {
    for (const n of graph.nodes) if (n.type !== 'end' && needing.has(n.storySceneId)) mark(n.id, { missingMedia: true });
  }

  const result = validateGraph(graph, scenes);
  const dangling = new Set(result.danglingEdgeIds);
  for (const n of graph.nodes) {
    if (n.type === 'end') continue;
    const name = scenes[n.storySceneId]?.name;
    const named = name !== undefined && result.errors.some((e) => e.includes(`cene "${name}"`));
    const byId = result.errors.some((e) => e.startsWith(`Node ${n.id} `));
    const edge = graph.edges.some((e) => e.from === n.id && dangling.has(e.id));
    if (named || byId || edge) mark(n.id, { needsFix: true });
  }
  return { badges, storyWideMissing };
}
